<template>
  <div class="col-sm-12">
    <div class="card custom-card">

      <!-- Bank Selection Dropdown -->
      <div class="form-group">
        <label for="Bank" class="block mb-2 font-semibold">
          Bank Name : <span class="text-red-500">*</span>
        </label>
        <select
            v-model="selectedBank"
            id="Bank"
            class="custom-select"
            :disabled="isSaving"
        >
          <option disabled value="">Select Bank</option>
          <option v-for="Bank in BankList" :key="Bank.id" :value="Bank.id">
            {{ Bank.bank_name }}
          </option>
        </select>
      </div>

      <!-- Branch Selection Dropdown -->
      <div class="form-group">
        <label for="Branch" class="block mb-2 font-semibold">
          Branch Name : <span class="text-red-500">*</span>
        </label>
        <select
            v-model="selectedBranch"
            id="Branch"
            class="custom-select"
            :disabled="isSaving || !selectedBank"
        >
          <option disabled value="">Select Branch</option>
          <option v-for="Branch in BranchList" :key="Branch.id" :value="Branch.id">
            {{ Branch.branch_name }}
          </option>
        </select>
      </div>

      <!-- Account Name Input -->
      <div class="form-group">
        <label for="name" class="block mb-2 font-semibold">
          Account Name : <span class="text-red-500">*</span>
        </label>
        <input
            type="text"
            id="name"
            v-model="name"
            placeholder="Enter Account Name"
            class="name-input"
            :disabled="isSaving"
        />
      </div>

      <!-- Account Number Input -->
      <div class="form-group">
        <label for="accountNumber" class="block mb-2 font-semibold">
          Account No : <span class="text-red-500">*</span>
        </label>
        <input
            type="text"
            id="accountNumber"
            v-model="accountNumber"
            placeholder="Enter Account No"
            class="name-input"
            :disabled="isSaving"
        />
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end action-buttons">
        <button
            type="button"
            class="btn btn-danger"
            @click="Cancel"
            :disabled="isSaving"
        >
          Cancel
        </button>
        <button
            type="button"
            class="btn btn-success save-button"
            @click="saveBanks"
            :disabled="isSaving || !isFormValid"
        >
          <span v-if="!isSaving">Save</span>
          <div v-else class="loading-indicator">
            <span class="spinner"></span>
            <span>Saving...</span>
          </div>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { getBankList, fetchBranchList, saveBankDetails } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId"),
      userCategory: localStorage.getItem("user_label"),
      router: useRouter(),
      BankList: [],
      BranchList: [],
      selectedBank: "",
      selectedBranch: "",
      name: "",
      accountNumber: "",
      isSaving: false,
    }
  },

  computed: {
    isFormValid() {
      return this.selectedBank &&
          this.selectedBranch &&
          this.name.trim() &&
          this.accountNumber.trim();
    }
  },

  watch: {
    selectedBank(newBankId) {
      if (newBankId) {
        this.loadBranchList();
        this.selectedBranch = ""; // Reset branch when bank changes
      } else {
        this.BranchList = [];
        this.selectedBranch = "";
      }
    }
  },

  created() {
    this.loadBankList();
  },

  methods: {
    async loadBankList() {
      try {
        const response = await getBankList(this.token);
        this.BankList = response.data.data || [];
        console.log('Fetched BankList:', this.BankList);
      } catch (error) {
        console.error('Error fetching BankList:', error);
      }
    },

    async loadBranchList() {
      if (!this.selectedBank) return;

      const payload = {
        bank_id: this.selectedBank,
      };

      try {
        const response = await fetchBranchList(payload, this.token);
        this.BranchList = response.data.data || [];
        console.log('Fetched BranchList:', this.BranchList);
      } catch (error) {
        console.error("Error fetching branches:", error.response);
        this.BranchList = [];
      }
    },

    async saveBanks() {
      if (!this.selectedBank) {
        useToast().error("Please select a bank.");
        return;
      }

      if (!this.selectedBranch) {
        useToast().error("Please select a branch.");
        return;
      }

      if (!this.name.trim()) {
        useToast().error("Please enter an account name.");
        return;
      }

      if (!this.accountNumber.trim()) {
        useToast().error("Please enter an account number.");
        return;
      }

      // Set saving state
      this.isSaving = true;

      let idToUse = this.profileId;
      if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
        idToUse = this.reportingProfileId;
      }

      const payload = {
        profile_id: idToUse,
        branch_id: this.selectedBranch,
        account_no: this.accountNumber,
        account_name: this.name,
        isactve: 1, // Always active for new bank account creation
      };

      try {
        const response = await saveBankDetails(this.token, payload);
        useToast().success("Bank details added successfully!");
        this.$router.push({ path: "/distributor-banks" });
      } catch (error) {
        console.error("Error response:", error.response);
        useToast().error("Failed to save bank details. Please try again.");
      } finally {
        this.isSaving = false;
      }
    },

    Cancel() {
      if (!this.isSaving) {
        this.$router.push({ path: "/distributor-banks" });
      }
    }
  }
}
</script>

<style scoped lang="scss">
.custom-card {
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 5px;
  padding: 16px;
}

.custom-select {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px;
  width: 100%;
  margin-top: 8px;
  font-size: 16px;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background-color: #e9ecef;
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.name-input {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px;
  width: 100%;
  margin-top: 8px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background-color: #e9ecef;
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.action-buttons {
  padding: 16px;
  gap: 12px;

  .btn {
    padding: 10px 20px;
    border-radius: 4px;
    font-weight: 500;
    min-width: 100px;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.save-button {
  position: relative;
  min-width: 120px;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.text-red-500 {
  color: #dc3545;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .custom-card {
    padding: 16px;
  }

  .form-group {
    padding: 12px;
  }
}
</style>